import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Pagination,
  Menu, // Material UI Menu
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import React from "react";
import { useGetOrdersQuery, useUpdateOrderStatusMutation } from "../../../store/api/orderAPI";
import { ORDER_STATUS } from "../../../constants/enums";
import moment from "moment";
import { showToast, showError } from "../../../constants/toasts";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const OrderManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [orderTab, setOrderTab] = useState(0);

  // Fetch orders from API
  const { data: ordersData, isLoading, isError } = useGetOrdersQuery({
    search: searchTerm,
  });

  const orders = ordersData?.data?.order || [];

  const tabLabels = [
    "Confirmed",
    "Preparing",
    "Ready/Packed",
    "On The Way",
    "Delivered",
    "Cancelled"
  ];

  // Map tabs to statuses
  // 0: ACCEPTED (1)
  // 1: PREPARING (2)
  // 2: READY_FOR_PICK_UP (3)
  // 3: ON_THE_WAY (5)
  // 4: DELIVERED (6)
  // 5: CANCELLED (7 & 8)

  const getStatusForTab = (tabIndex: number): number[] => {
    switch (tabIndex) {
      case 0: return [ORDER_STATUS.ACCEPTED];
      case 1: return [ORDER_STATUS.PREPARING];
      case 2: return [ORDER_STATUS.READY_FOR_PICK_UP, ORDER_STATUS.PICKED_UP];
      case 3: return [ORDER_STATUS.ON_THE_WAY];
      case 4: return [ORDER_STATUS.DELIVERED];
      case 5: return [ORDER_STATUS.CANCELLED_BY_USER, ORDER_STATUS.CANCELLED_BY_STORE];
      default: return [];
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case ORDER_STATUS.PENDING: return "Pending";
      case ORDER_STATUS.ACCEPTED: return "Accepted";
      case ORDER_STATUS.PREPARING: return "Preparing";
      case ORDER_STATUS.READY_FOR_PICK_UP: return "Ready for Pickup";
      case ORDER_STATUS.PICKED_UP: return "Picked Up";
      case ORDER_STATUS.ON_THE_WAY: return "On the Way";
      case ORDER_STATUS.DELIVERED: return "Delivered";
      case ORDER_STATUS.CANCELLED_BY_USER: return "Cancelled by User";
      case ORDER_STATUS.CANCELLED_BY_STORE: return "Cancelled by Store";
      default: return "Unknown";
    }
  };

  const [storeFilter, setStoreFilter] = React.useState('default');
  const handleStoreChange = (event: SelectChangeEvent) => {
    setStoreFilter(event.target.value as string);
  };

  // Status Update Logic
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<number | null>(null);
  const openMenu = Boolean(anchorEl);
  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, orderId: string, status: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
    setSelectedOrderStatus(status);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedOrderId(null);
    setSelectedOrderStatus(null);
  };

  const handleStatusUpdate = async (status: number) => {
    if (!selectedOrderId) return;
    try {
      await updateOrderStatus({ orderId: selectedOrderId, status }).unwrap();
      showToast("Order status updated successfully");
      handleCloseMenu();
    } catch (error: any) {
      console.error("Failed to update status", error);
      showError(error?.data?.message || "Failed to update status");
    }
  };

  const filteredOrders = orders.filter(order => {
    const validStatuses = getStatusForTab(orderTab);
    return validStatuses.includes(order.status);
  });

  return (
    <>
      <div className="main_title">
        <h1>Order Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Order Management
        </p>
      </div>

      {/* Tabs */}
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={orderTab}
              onChange={(e, newValue) => setOrderTab(newValue)}
              className="custom_tabs_links"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      {/* Table */}
      <div className="cards">
        <div className="table_header">
          <Box className="search_box">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search orders..."
            />
            {/* Store Filter - Placeholder logic for now as API doesn't confirm store filtering explicitly yet */}
            <div className="form">
              <Select
                fullWidth
                value={storeFilter}
                onChange={handleStoreChange}
                displayEmpty
              >
                <MenuItem value="default">All Stores</MenuItem>
                {/* Populate dynamically if stores are available */}
              </Select>
            </div>
          </Box>
        </div>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <div className="error_message">Error loading orders</div>
        ) : (
          <CustomTabPanel value={orderTab} index={orderTab}>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>User Details</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredOrders.length > 0 ? (
                    filteredOrders.map((order, index) => (
                      <TableRow hover key={order._id || index}>
                        <TableCell>
                          <span className="order_id" onClick={() => navigate('view', { state: { order } })} style={{ cursor: 'pointer', color: 'blue' }}>
                            #{order._id.substring(order._id.length - 6).toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Box className="user_block">
                            <figure>
                              <img
                                src={order.user?.profilePhoto || "/static/images/user_placeholder.png"}
                                alt="User"
                                onError={(e) => { (e.target as HTMLImageElement).src = "/static/images/user_placeholder.png" }}
                              />
                            </figure>
                            <p>
                              <b>{order.deliveryAddress?.name || "N/A"}</b>
                              <span>{order.deliveryAddress?.phone || "N/A"}</span>
                            </p>
                          </Box>
                        </TableCell>
                        <TableCell>${order.grandTotal?.toFixed(2)}</TableCell>
                        <TableCell>
                          {order.suborders?.length || 0} Items
                          {order.suborders?.[0] && (
                            <span style={{ display: 'block', fontSize: '11px', color: '#888' }}>
                              ({order.suborders[0].product?.name}...)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {order.paymentMode === 1 ? "Cash" : "Online"}
                          <br />
                          <span className={`status_tag ${order.paymentStatus === 1 ? 'pending' : 'success'}`} style={{ fontSize: '11px' }}>
                            {order.paymentStatus === 1 ? "Pending" : order.paymentStatus === 2 ? "Processing" : order.paymentStatus === 3 ? "Completed" : "Failed"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {moment(order.createdAt).format("DD MMM YYYY")}
                          <br />
                          {moment(order.createdAt).format("hh:mm A")}
                        </TableCell>
                        <TableCell>
                          <span className="status_badge">
                            {getStatusText(order.status)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="table_actions v2">
                            <IconButton onClick={() => navigate('view', { state: { order } })}>
                              <img src="/static/images/eye_icon.svg" alt="View" />
                            </IconButton>
                            {orderTab !== 5 && orderTab !== 4 && (
                              <IconButton onClick={(e) => handleOpenMenu(e, order._id, order.status)}>
                                <img src="/static/images/edit2_icon.svg" alt="Update Status" />
                              </IconButton>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No orders found in this category.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTabPanel>
        )}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
      >
        {Object.values(ORDER_STATUS)
          .filter((status) => typeof status === "number")
          .filter((status) => {
            const s = status as number;
            const currentStatus = selectedOrderStatus || 0;

            // Define the standard workflow options
            const validOptions: number[] = [
              ORDER_STATUS.ACCEPTED,
              ORDER_STATUS.PREPARING,
              ORDER_STATUS.READY_FOR_PICK_UP,
              ORDER_STATUS.ON_THE_WAY,
              ORDER_STATUS.DELIVERED
            ];

            // Always allow cancelling if not already cancelled or delivered
            if (s === ORDER_STATUS.CANCELLED_BY_STORE) {
              return currentStatus < ORDER_STATUS.DELIVERED;
            }

            // Only show standard workflow options
            if (!validOptions.includes(s)) return false;

            // Show strictly future statuses
            return s > currentStatus;
          })
          .map((status) => (
            <MenuItem
              key={status}
              onClick={() => handleStatusUpdate(status as number)}
            >
              {getStatusText(status as number)}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default OrderManagement;
